"use client";

import { useCallback, useState } from "react";
import { formatCentsAsCurrency } from "@/lib/forms/format-price";

/** What the caller's `submitForm` must resolve to for the payment flow to proceed. */
interface SubmitResult {
  responseId: number | null;
  /** The response's edit receipt: the payment routes take it as proof of ownership. */
  editToken: string | null;
  /** Lets this client trigger the confirmation email once the registration is paid (or free). */
  confirmToken: string | null;
}

interface UseFormPaymentResult {
  hasPayment: boolean;
  showCheckout: boolean;
  paymentClientSecret: string | null;
  paymentResponseId: number | null;
  /** The response's edit receipt, which CheckoutStep polls payment-status with. */
  paymentEditToken: string | null;
  /**
   * Captured at submit time, the fallback for the deferred confirmation email when the paid poll
   * returns no fresh token (CheckoutStep hands that one to `onComplete`).
   */
  paymentConfirmToken: string | null;
  resolvedAmountDisplay: string;
  /**
   * Runs the submit-then-pay flow: submits the form (via `submitForm`, owned by the
   * caller since it depends on form-field state this hook doesn't have), then creates
   * a PaymentIntent for the resulting response. If nothing needs charging (a free
   * tier resolved), calls `onFreeCompletion` directly instead of opening the checkout
   * step — there's no payment step left to show.
   *
   * @throws When the response or its edit token can't be resolved, or PaymentIntent creation fails.
   */
  preparePayment: (
    submitForm: () => Promise<SubmitResult>,
    onFreeCompletion: (responseId: number, confirmToken: string | null) => void | Promise<void>
  ) => Promise<void>;
  closeCheckout: () => void;
}

/**
 * Owns the submit-then-pay orchestration for FormModal: creating a PaymentIntent for
 * a just-submitted response and tracking the state CheckoutStep needs to render.
 *
 * @param paymentEnabled - The form's `payment_enabled` setting. This alone gates
 *   `hasPayment` — the actual amount (respondent-type price, legacy flat price, or
 *   free) is resolved server-side by create-payment-intent, which already returns
 *   `noPaymentIntent: true` when there's nothing to charge.
 */
export function useFormPayment(paymentEnabled: boolean): UseFormPaymentResult {
  const hasPayment = paymentEnabled;

  // States
  const [showCheckout, setShowCheckout] = useState(false);
  // Populated by the submit-then-pay flow: the response is saved (status: submitted,
  // payment_status: pending) BEFORE the card form ever renders, since PaymentElement
  // needs a real clientSecret to mount at all.
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentResponseId, setPaymentResponseId] = useState<number | null>(null);
  const [paymentEditToken, setPaymentEditToken] = useState<string | null>(null);
  const [paymentConfirmToken, setPaymentConfirmToken] = useState<string | null>(null);
  // The real, resolved amount from create-payment-intent — may differ from the legacy
  // flat paymentAmountDisplay string once a respondent-type price applies.
  const [resolvedAmountDisplay, setResolvedAmountDisplay] = useState("");

  // Handlers
  const preparePayment = useCallback(
    async (
      submitForm: () => Promise<SubmitResult>,
      onFreeCompletion: (responseId: number, confirmToken: string | null) => void | Promise<void>
    ) => {
      const { responseId, editToken, confirmToken } = await submitForm();
      if (!responseId || !editToken) {
        throw new Error("Could not resolve the submitted response for payment");
      }

      // The edit token, not an email, proves this client owns the response: an email lookup
      // would let anyone probe who registered.
      const piResponse = await fetch("/api/forms/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responseId, token: editToken }),
      });
      const piData = await piResponse.json();

      if (piData.noPaymentIntent) {
        // Nothing to charge (free tier resolved) — there's no payment step left to
        // defer to, so the caller finalizes the submission now.
        await onFreeCompletion(responseId, confirmToken);
        return;
      }
      if (!piResponse.ok || !piData.clientSecret) {
        throw new Error(piData.error || "Failed to prepare payment");
      }

      setPaymentResponseId(responseId);
      setPaymentEditToken(editToken);
      setPaymentConfirmToken(confirmToken);
      setPaymentClientSecret(piData.clientSecret);
      setResolvedAmountDisplay(formatCentsAsCurrency(piData.amountMinorUnits, piData.currency));
      setShowCheckout(true);
    },
    []
  );

  const closeCheckout = useCallback(() => setShowCheckout(false), []);

  return {
    hasPayment,
    showCheckout,
    paymentClientSecret,
    paymentResponseId,
    paymentEditToken,
    paymentConfirmToken,
    resolvedAmountDisplay,
    preparePayment,
    closeCheckout,
  };
}
